import React, { useState, useEffect } from 'react';
import { inverterTrendService, DailyPerformanceData } from '../../services/inverterTrendService';

interface DailyPerformanceChartProps {
  inverterId: string;
  dateRange?: string;
  plantId?: string;
  visibleMetrics: string[];
}

const DailyPerformanceChart: React.FC<DailyPerformanceChartProps> = ({
  inverterId,
  dateRange,
  plantId,
  visibleMetrics
}) => {
  const [data, setData] = useState<DailyPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState<{ index: number; metric: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [inverterId, dateRange, plantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Mock data for visualization
      const mockData: DailyPerformanceData[] = Array.from({ length: 10 }, (_, i) => ({
        date: `2025-10-${String(i + 1).padStart(2, '0')}`,
        production_kwh: 2500 + Math.sin(i * 0.5) * 500 + Math.random() * 200,
        specific_yield: 4.2 + Math.sin(i * 0.3) * 0.8 + Math.random() * 0.3,
        pr: 85 + Math.sin(i * 0.4) * 5 + Math.random() * 3,
        efficiency: 92 + Math.sin(i * 0.2) * 3 + Math.random() * 2,
        cuf_dc: 28 + Math.sin(i * 0.6) * 8 + Math.random() * 4,
        cuf_ac: 26 + Math.sin(i * 0.6) * 7 + Math.random() * 3,
        trinomic_loss: 4 + Math.random() * 2
      }));
      setData(mockData);
      
      // Uncomment to use real API
      // const response = await inverterTrendService.getPerformanceTrend(inverterId, dateRange, plantId);
      // setData(response.data);
    } catch (error) {
      console.error('Error loading trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Production (kWh)', 'Specific Yield (kWh/kWp)', 'Daily PR (%)', 'Efficiency (%)', 'CUF DC (%)', 'CUF AC (%)', 'Trinomic Loss (%)'];
    const csvData = data.map(item => [
      item.date,
      item.production_kwh,
      item.specific_yield,
      item.pr,
      item.efficiency,
      item.cuf_dc,
      item.cuf_ac,
      item.trinomic_loss
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${inverterId}_daily_performance.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const maxProduction = Math.max(...data.map(d => d.production_kwh));
  const maxYield = Math.max(...data.map(d => d.specific_yield));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Daily Performance Trend - {inverterId}</h3>
        <div className="flex space-x-2">
          <button
            onClick={exportToCSV}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={!data.length}
          >
            Export CSV
          </button>
        </div>
      </div>
      
      {/* Chart Container */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="relative h-80">
          <svg width="100%" height="100%" viewBox="0 0 800 300">
            {/* Grid Lines */}
            {[0, 1, 2, 3, 4].map(i => (
              <line
                key={i}
                x1="60"
                y1={60 + i * 48}
                x2="740"
                y2={60 + i * 48}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            ))}
            
            {/* Y-Axis Labels */}
            <text x="20" y="65" className="text-xs fill-gray-600">
              {maxProduction.toFixed(0)}
            </text>
            <text x="20" y="113" className="text-xs fill-gray-600">
              {(maxProduction * 0.75).toFixed(0)}
            </text>
            <text x="20" y="161" className="text-xs fill-gray-600">
              {(maxProduction * 0.5).toFixed(0)}
            </text>
            <text x="20" y="209" className="text-xs fill-gray-600">
              {(maxProduction * 0.25).toFixed(0)}
            </text>
            <text x="20" y="257" className="text-xs fill-gray-600">0</text>
            
            {/* Bars and Lines */}
            {data.map((item, index) => {
              const x = 60 + (index * (680 / data.length));
              const barHeight = (item.production_kwh / maxProduction) * 192;
              const yieldHeight = (item.specific_yield / maxYield) * 192;
              
              return (
                <g key={index}>
                  {/* Production Bar */}
                  {visibleMetrics.includes('production') && (
                    <g>
                      <rect
                        x={x}
                        y={252 - barHeight}
                        width="20"
                        height={barHeight}
                        fill="#93C5FD"
                        className="hover:fill-blue-400 cursor-pointer"
                        onMouseEnter={() => setHoveredPoint({ index, metric: 'production' })}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                      {/* Data Label */}
                      <text
                        x={x + 10}
                        y={252 - barHeight - 5}
                        className="text-xs fill-blue-600"
                        textAnchor="middle"
                      >
                        {item.production_kwh.toFixed(0)}
                      </text>
                    </g>
                  )}
                  
                  {/* Specific Yield Line Point */}
                  {visibleMetrics.includes('yield') && (
                    <g>
                      <circle
                        cx={x + 10}
                        cy={252 - yieldHeight}
                        r="3"
                        fill="#8B5CF6"
                        className="hover:r-4 cursor-pointer"
                        onMouseEnter={() => setHoveredPoint({ index, metric: 'yield' })}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                      {/* Data Label */}
                      <text
                        x={x + 25}
                        y={252 - yieldHeight + 3}
                        className="text-xs fill-purple-600"
                        textAnchor="start"
                      >
                        {item.specific_yield.toFixed(1)}
                      </text>
                    </g>
                  )}
                  
                  {/* PR Line Point */}
                  {visibleMetrics.includes('pr') && (
                    <g>
                      <circle
                        cx={x + 10}
                        cy={252 - (item.pr / 100) * 192}
                        r="3"
                        fill="#1E40AF"
                        className="hover:r-4 cursor-pointer"
                        onMouseEnter={() => setHoveredPoint({ index, metric: 'pr' })}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                      {/* Data Label */}
                      <text
                        x={x + 25}
                        y={252 - (item.pr / 100) * 192 - 5}
                        className="text-xs fill-blue-800"
                        textAnchor="start"
                      >
                        {item.pr.toFixed(1)}%
                      </text>
                    </g>
                  )}
                  
                  {/* Date Label */}
                  <text
                    x={x + 10}
                    y="275"
                    className="text-xs fill-gray-600"
                    textAnchor="middle"
                  >
                    {new Date(item.date).getDate()}
                  </text>
                </g>
              );
            })}
            
            {/* Connect Yield Line */}
            {visibleMetrics.includes('yield') && data.length > 1 && (
              <polyline
                points={data.map((item, index) => {
                  const x = 60 + (index * (680 / data.length)) + 10;
                  const y = 252 - (item.specific_yield / maxYield) * 192;
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#8B5CF6"
                strokeWidth="2"
              />
            )}
            
            {/* Connect PR Line */}
            {visibleMetrics.includes('pr') && data.length > 1 && (
              <polyline
                points={data.map((item, index) => {
                  const x = 60 + (index * (680 / data.length)) + 10;
                  const y = 252 - (item.pr / 100) * 192;
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#1E40AF"
                strokeWidth="2"
              />
            )}
          </svg>
        </div>
        
        {/* Tooltip */}
        {hoveredPoint && (
          <div className="absolute top-4 right-4 bg-gray-800 text-white p-2 rounded text-sm">
            <div>Date: {data[hoveredPoint.index]?.date}</div>
            {hoveredPoint.metric === 'production' && (
              <div>Production: {data[hoveredPoint.index]?.production_kwh.toFixed(2)} kWh</div>
            )}
            {hoveredPoint.metric === 'yield' && (
              <div>Specific Yield: {data[hoveredPoint.index]?.specific_yield.toFixed(2)} kWh/kWp</div>
            )}
            {hoveredPoint.metric === 'pr' && (
              <div>Daily PR: {data[hoveredPoint.index]?.pr.toFixed(2)}%</div>
            )}
          </div>
        )}
        
        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mt-4">
          {visibleMetrics.includes('production') && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-300 rounded"></div>
              <span className="text-sm">Production (kWh)</span>
            </div>
          )}
          {visibleMetrics.includes('yield') && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-1 bg-purple-500"></div>
              <span className="text-sm">Specific Yield (kWh/kWp)</span>
            </div>
          )}
          {visibleMetrics.includes('pr') && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-1 bg-blue-800"></div>
              <span className="text-sm">Daily PR (%)</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyPerformanceChart;